import { useDeckBinding } from './DeckDataContext';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useChatStore } from '@/store';
import { chatsApi } from '@/lib/api';
import ChatView from '@/components/Chat/ChatView';
import DeckColumnHeader from './DeckColumnHeader';
import DeckChatPicker from './DeckChatPicker';
import DeckNewChatComposer from './DeckNewChatComposer';
import { deckPaneStyles } from './deckPaneStyles';

const CHAT_DELETED_PROBE_MS = 500;

const DeckColumn = React.memo(function DeckColumn({
  column,
  deckSlug,
  columnIndex,
  totalColumns,
  isFocused = false,
  onDragStart,
  setRef,
  isBeingDragged = false,
  isAnimatingIn = false,
  isRemoving = false,
}) {
  const { store: useDeckStore } = useDeckBinding();
  const { colors } = useTheme();
  const { conversationId, width } = column;
  const columnId = column.id;
  // Selecting `state.actions` directly is safe: zustand stores keep the
  // same reference across renders, so we don't churn subscriptions.
  const deckActions = useDeckStore(state => state.actions);
  const { removeColumn, setColumnConversation, setFocusedColumnId } = deckActions;

  const handleContainerRef = useCallback((node) => {
    setRef?.(columnId, node);
  }, [setRef, columnId]);

  const handleDragStart = useCallback((e) => {
    onDragStart?.(columnId, e);
  }, [onDragStart, columnId]);

  const chat = useChatStore(state =>
    conversationId ? state.chats.find(c => c.id === conversationId) : null
  );
  const addChats = useChatStore(state => state.addChats);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatDeleted, setChatDeleted] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const voiceModeTarget = useMemo(() => ({ deckSlug, columnId }), [deckSlug, columnId]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setChatDeleted(false);
    setIsLoading(false);
    if (!conversationId) return;

    const fetchChat = async () => {
      try {
        const freshChats = useChatStore.getState().chats;
        const existing = freshChats.find(c => c.id === conversationId);
        if (existing && existing.messages?.length > 0) {
          setIsLoading(false);
          setChatDeleted(false);
          return;
        }

        setIsLoading(true);
        setError(null);
        setChatDeleted(false);

        const data = await chatsApi.getConversationById(conversationId);
        if (cancelled) return;

        if (!data) {
          throw new Error('Chat not found');
        }

        const freshAfterFetch = useChatStore.getState().chats;
        const addedWhileFetching = freshAfterFetch.find(c => c.id === conversationId);
        if (addedWhileFetching && addedWhileFetching.messages?.length > 0) {
          setIsLoading(false);
          return;
        }

        addChats([data]);
      } catch (err) {
        if (cancelled) return;
        console.error('[DeckColumn] Error fetching chat:', err);
        if (err.status === 404) {
          setChatDeleted(true);
        } else {
          setError(err.message || 'Failed to load chat');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchChat();
    return () => { cancelled = true; };
  }, [conversationId, addChats]);

  useEffect(() => {
    if (conversationId && !chat && !isLoading && !error) {
      const timer = setTimeout(() => {
        const freshChat = useChatStore.getState().chats.find(c => c.id === conversationId);
        if (!freshChat) {
          setChatDeleted(true);
        }
      }, CHAT_DELETED_PROBE_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [conversationId, chat, isLoading, error]);

  const handleRemove = useCallback(() => {
    removeColumn(columnId, deckSlug);
  }, [columnId, deckSlug, removeColumn]);

  const handleSelectChat = useCallback((chatId) => {
    setColumnConversation(columnId, chatId, deckSlug);
    setChatDeleted(false);
    setError(null);
  }, [columnId, deckSlug, setColumnConversation]);

  const handleChangeChat = useCallback(() => {
    setColumnConversation(columnId, null, deckSlug);
    setChatDeleted(false);
    setError(null);
    setIsComposing(false);
  }, [columnId, deckSlug, setColumnConversation]);

  const handleNewConversation = useCallback(() => setIsComposing(true), []);

  const handleComposeCancel = useCallback(() => setIsComposing(false), []);

  const handleConversationStarted = useCallback((chatId) => {
    setColumnConversation(columnId, chatId, deckSlug);
    setIsComposing(false);
  }, [columnId, deckSlug, setColumnConversation]);

  const handleFocus = useCallback(() => {
    setFocusedColumnId(columnId);
  }, [columnId, setFocusedColumnId]);

  const containerStyle = useMemo(() => [
    deckPaneStyles.pane,
    {
      width: isRemoving ? 0 : width,
      borderColor: isFocused ? colors.border_color_primary : colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
      opacity: isAnimatingIn || isRemoving ? 0 : isBeingDragged ? 0.5 : 1,
    }
  ], [width, colors, isFocused, isBeingDragged, isAnimatingIn, isRemoving]);

  if (!conversationId || chatDeleted) {
    return (
      <View ref={handleContainerRef} style={containerStyle} onPointerDown={handleFocus}>
        <DeckColumnHeader
          chat={null}
          titleOverride={isComposing ? 'New conversation' : undefined}
          columnId={columnId}
          columnIndex={columnIndex}
          totalColumns={totalColumns}
          onRemove={handleRemove}
          onChangeChat={isComposing ? handleComposeCancel : handleChangeChat}
          isFocused={isFocused}
          onDragStart={handleDragStart}
        />
        {isComposing ? (
          <DeckNewChatComposer
            deckSlug={deckSlug}
            columnId={columnId}
            onConversationStarted={handleConversationStarted}
            onFocus={handleFocus}
          />
        ) : (
          <DeckChatPicker
            onSelectChat={handleSelectChat}
            onNewConversation={handleNewConversation}
          />
        )}
      </View>
    );
  }

  const showLoading = !chat && !error;

  return (
    <View ref={handleContainerRef} style={containerStyle} onPointerDown={handleFocus}>
      <DeckColumnHeader
        chat={chat}
        columnId={columnId}
        columnIndex={columnIndex}
        totalColumns={totalColumns}
        onRemove={handleRemove}
        onChangeChat={handleChangeChat}
        isFocused={isFocused}
        onDragStart={handleDragStart}
      />
      <ChatView
        conversationId={conversationId}
        chat={chat}
        isLoading={showLoading || isLoading}
        error={error}
        isCompact={true}
        onFocus={handleFocus}
        voiceModeTarget={voiceModeTarget}
      />
    </View>
  );
});

export default DeckColumn;
